import React, { useContext, useState, useCallback, useMemo, useRef, useEffect } from "react"
import { firestore } from "../firebase/firebase"

const UserContext = React.createContext()

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider({ children }) {

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState('');

  // A single real-time listener is attached on first use (lazy) and shared by
  // every consumer, so pages that never ask for users pay nothing, and the
  // whole-collection read happens once — subsequent edits stream in as deltas.
  const unsubRef = useRef(null);          // active onSnapshot unsubscribe fn
  const startedRef = useRef(false);       // has the listener been attached?
  const usersRef = useRef([]);            // latest snapshot (for promise callers)
  const firstSnapPromise = useRef(null);  // resolves on the first snapshot
  const resolveFirstSnap = useRef(null);

  const startListener = useCallback(() => {
    if (startedRef.current) return firstSnapPromise.current;
    startedRef.current = true;
    setUsersLoading(true);
    firstSnapPromise.current = new Promise((resolve) => {
      resolveFirstSnap.current = resolve;
    });

    unsubRef.current = firestore.collection("users").onSnapshot(
      (querySnapshot) => {
        const userResponse = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          'docId': doc.id,
        }));
        usersRef.current = userResponse;
        setUsers(userResponse);
        setUsersLoading(false);
        setError('');
        if (resolveFirstSnap.current) {
          resolveFirstSnap.current(userResponse);
          resolveFirstSnap.current = null;
        }
      },
      (err) => {
        setUsersLoading(false);
        setError(err.message);
        console.log(err.message);
        // allow a future attempt to re-attach if the listen failed
        startedRef.current = false;
        unsubRef.current = null;
        if (resolveFirstSnap.current) {
          resolveFirstSnap.current([]);
          resolveFirstSnap.current = null;
        }
      }
    );

    return firstSnapPromise.current;
  }, []);

  // detach the listener when the provider unmounts
  useEffect(() => () => { if (unsubRef.current) unsubRef.current(); }, []);

  // fire-and-forget trigger (signature unchanged); attaches the listener once
  const getUsers = useCallback(() => { startListener(); }, [startListener]);

  // resolves with the latest users once the first snapshot has arrived
  const getUsersPromise = useCallback(async () => {
    await startListener();
    return usersRef.current;
  }, [startListener]);

  const getUser = useCallback((uuid) => {
    return users?.filter(user => user.uuid === uuid);
  }, [users]);

  const value = useMemo(() => ({
    users,
    usersLoading,
    error,
    getUsers,
    getUsersPromise,
    getUser,
  }), [users, usersLoading, error, getUsers, getUsersPromise, getUser])

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}
